import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Download, Upload, RefreshCw, User, Calendar, MapPin, Clock, 
  FileSpreadsheet, Trash2, CheckCircle, AlertCircle, Loader2,
  ChevronDown, ChevronRight, Sun, Moon as MoonIcon, Star, Home,
  Grid3X3, BarChart2, Compass, Eye, Layers, Activity, Target, 
  Sparkles, Globe, Zap, Users, FileJson, Package, Search, Database,
  BookOpen, Play, Lightbulb, TrendingUp, Heart, GraduationCap, Plane, Gem
} from 'lucide-react';
import { ChartToggle } from '../components/charts';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Celebrity Dataset Download Section
const CelebrityDownloadSection = () => {
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/celebrity-dataset/birth-details-info`);
        const data = await res.json();
        setDatasetInfo(data);
      } catch (e) {
        console.error('Error fetching dataset info:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, []);

  const handleDownload = async (format) => {
    setDownloadingFormat(format);
    try {
      const endpoint = format === 'json' 
        ? '/api/celebrity-dataset/birth-details-json'
        : '/api/celebrity-dataset/birth-details-csv';
      
      const response = await fetch(`${API_URL}${endpoint}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'json' 
        ? 'all_celebrities_birth_details.json' 
        : 'all_celebrities_birth_details.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download error:', e);
    } finally {
      setDownloadingFormat(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-2xl p-6 border border-purple-500/30">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
          <span className="text-gray-400">Loading celebrity dataset info...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-2xl border border-purple-500/30 overflow-hidden">
      {/* Header - Always visible */}
      <div 
        className="p-6 cursor-pointer hover:bg-purple-500/5 transition-colors flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Database className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              Celebrity Birth Details Database
              <span className="text-sm font-normal text-purple-400 bg-purple-500/20 px-2 py-0.5 rounded">
                {datasetInfo?.total_celebrities?.toLocaleString() || '24,726'} People
              </span>
            </h2>
            <p className="text-gray-400 text-sm">AA & A rated verified birth times from Astro-Databank</p>
          </div>
        </div>
        <ChevronDown className={`w-6 h-6 text-purple-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-6 pb-6 pt-2 border-t border-purple-500/20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* JSON Download */}
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <FileJson className="w-8 h-8 text-amber-400" />
                <div>
                  <h3 className="text-white font-medium">JSON Format</h3>
                  <p className="text-gray-500 text-xs">
                    {datasetInfo?.files?.json?.size_mb || '4.5'} MB • Best for programming
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('json')}
                disabled={downloadingFormat === 'json'}
                className="w-full py-2.5 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="download-celebrity-json"
              >
                {downloadingFormat === 'json' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download JSON
                  </>
                )}
              </button>
            </div>

            {/* CSV Download */}
            <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <FileSpreadsheet className="w-8 h-8 text-green-400" />
                <div>
                  <h3 className="text-white font-medium">CSV Format</h3>
                  <p className="text-gray-500 text-xs">
                    {datasetInfo?.files?.csv?.size_mb || '1.25'} MB • Best for Excel
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDownload('csv')}
                disabled={downloadingFormat === 'csv'}
                className="w-full py-2.5 bg-green-500 text-black font-semibold rounded-lg hover:bg-green-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="download-celebrity-csv"
              >
                {downloadingFormat === 'csv' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download CSV
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Data Fields Info */}
          <div className="bg-gray-800/30 rounded-lg p-4">
            <h4 className="text-gray-300 font-medium mb-2">Fields Included:</h4>
            <div className="flex flex-wrap gap-2">
              {(datasetInfo?.fields || ['name', 'birth_date', 'birth_time', 'birth_place', 'latitude', 'longitude']).map(field => (
                <span key={field} className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                  {field}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Profile Search Component
const ProfileSearch = ({ onSelectProfile }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const searchRef = useRef(null);

  // Fetch total profiles count on mount
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API_URL}/api/unified-kernel/profiles/count`);
        const data = await res.json();
        if (data.success) {
          setTotalProfiles(data.count);
        }
      } catch (e) {
        console.error('Error fetching profile count:', e);
      }
    };
    fetchCount();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search profiles
  const handleSearch = async (query) => {
    setSearchQuery(query);
    
    if (query.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    setIsSearching(true);
    setShowDropdown(true);
    
    try {
      const res = await fetch(`${API_URL}/api/unified-kernel/profiles/search?q=${encodeURIComponent(query)}&limit=15`);
      const data = await res.json();
      if (data.success) {
        setSearchResults(data.profiles);
      }
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setIsSearching(false);
    }
  };

  // Select a profile
  const handleSelectProfile = (profile) => {
    setSearchQuery(profile.name);
    setShowDropdown(false);
    onSelectProfile(profile);
  };

  return (
    <div ref={searchRef} className="relative">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        <Database className="w-4 h-4 inline mr-2" />
        Search Saved Profiles ({totalProfiles} available)
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => searchQuery.length > 0 && setShowDropdown(true)}
          placeholder="Type name to search (e.g., Mandeep, Parveen...)"
          className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-amber-500/50 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          data-testid="profile-search-input"
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500 animate-spin" />
        )}
      </div>
      
      {/* Search Results Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          {searchResults.length === 0 ? (
            <div className="px-4 py-3 text-gray-400 text-center">
              {isSearching ? 'Searching...' : searchQuery.length > 0 ? 'No profiles found' : 'Type to search'}
            </div>
          ) : (
            searchResults.map((profile, idx) => (
              <div
                key={idx}
                onClick={() => handleSelectProfile(profile)}
                className="px-4 py-3 hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-b-0 transition-colors"
                data-testid={`profile-result-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-white font-medium">{profile.name}</span>
                    <span className="ml-2 text-xs text-gray-400">
                      ({profile.gender === 'male' ? 'M' : profile.gender === 'female' ? 'F' : '?'})
                    </span>
                  </div>
                  <span className="text-amber-500 text-sm">
                    {profile.day}/{profile.month}/{profile.year}
                  </span>
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  {profile.hour}:{String(profile.minute).padStart(2, '0')} | {profile.place || `${profile.latitude}, ${profile.longitude}`}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Birth Details Input Form Component
const BirthDetailsForm = ({ onSubmit, loading, initialData }) => {
  const [formData, setFormData] = useState({
    name: '',
    year: 1990,
    month: 1,
    day: 1,
    hour: 12,
    minute: 0,
    latitude: 28.6139,
    longitude: 77.2090,
    timezone_offset: 5.5,
    place: 'Delhi, India'
  });

  // Update form when initialData changes (from profile selection)
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        year: initialData.year || 1990,
        month: initialData.month || 1,
        day: initialData.day || 1,
        hour: initialData.hour || 12,
        minute: initialData.minute || 0,
        latitude: initialData.latitude || 28.6139,
        longitude: initialData.longitude || 77.2090,
        timezone_offset: initialData.timezone_offset || 5.5,
        place: initialData.place || 'Delhi, India'
      });
    }
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  // Timezone validation - check if timezone matches longitude
  const getTimezoneWarning = () => {
    const expectedTz = Math.round(formData.longitude / 15);
    const currentTz = formData.timezone_offset;
    const tolerance = 2;
    
    if (Math.abs(currentTz - expectedTz) > tolerance) {
      return {
        show: true,
        message: `Warning: Timezone ${currentTz >= 0 ? '+' : ''}${currentTz} may not match longitude ${formData.longitude.toFixed(2)}°. Expected timezone around ${expectedTz >= 0 ? '+' : ''}${expectedTz}. Please verify.`,
        suggested: expectedTz
      };
    }
    return { show: false };
  };

  const timezoneWarning = getTimezoneWarning();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Name */}
        <div className="col-span-full md:col-span-1">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <User className="w-4 h-4 inline mr-2" />Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter name"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            data-testid="input-name"
          />
        </div>

        {/* Date */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <Calendar className="w-4 h-4 inline mr-2" />Year
          </label>
          <input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleChange}
            min="1"
            max="2100"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-year"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Month</label>
          <select
            name="month"
            value={formData.month}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-month"
          >
            {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Day</label>
          <input
            type="number"
            name="day"
            value={formData.day}
            onChange={handleChange}
            min="1"
            max="31"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-day"
          />
        </div>

        {/* Time */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <Clock className="w-4 h-4 inline mr-2" />Hour (0-23)
          </label>
          <input
            type="number"
            name="hour"
            value={formData.hour}
            onChange={handleChange}
            min="0"
            max="23"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-hour"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Minute</label>
          <input
            type="number"
            name="minute"
            value={formData.minute}
            onChange={handleChange}
            min="0"
            max="59"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-minute"
          />
        </div>

        {/* Location */}
        <div className="col-span-full md:col-span-1">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <MapPin className="w-4 h-4 inline mr-2" />Place
          </label>
          <input
            type="text"
            name="place"
            value={formData.place}
            onChange={handleChange}
            placeholder="City, Country"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-amber-500"
            data-testid="input-place"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Latitude</label>
          <input
            type="number"
            name="latitude"
            value={formData.latitude}
            onChange={handleChange}
            step="0.0001"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-latitude"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Longitude</label>
          <input
            type="number"
            name="longitude"
            value={formData.longitude}
            onChange={handleChange}
            step="0.0001"
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500"
            data-testid="input-longitude"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Timezone (UTC offset)</label>
          <input
            type="number"
            name="timezone_offset"
            value={formData.timezone_offset}
            onChange={handleChange}
            step="0.5"
            min="-12"
            max="14"
            className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:ring-2 focus:ring-amber-500 ${timezoneWarning.show ? 'border-yellow-500' : 'border-gray-700'}`}
            data-testid="input-timezone"
          />
          {timezoneWarning.show && (
            <div className="mt-2 p-2 bg-yellow-900/30 border border-yellow-600/50 rounded-lg">
              <p className="text-yellow-400 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {timezoneWarning.message}
              </p>
              <button
                type="button"
                onClick={() => setFormData(prev => ({...prev, timezone_offset: timezoneWarning.suggested}))}
                className="mt-2 px-3 py-1 text-xs bg-yellow-600 text-black rounded hover:bg-yellow-500"
              >
                Use suggested: {timezoneWarning.suggested >= 0 ? '+' : ''}{timezoneWarning.suggested}
              </button>
            </div>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg hover:from-amber-400 hover:to-orange-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="generate-kernel-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Generating Kernel...
          </>
        ) : (
          <>
            <Zap className="w-5 h-5" />
            Generate Complete Kernel
          </>
        )}
      </button>
    </form>
  );
};

// Bulk Upload Component
const BulkUploadSection = ({ onBulkProcess, bulkLoading, bulkResults }) => {
  const fileInputRef = useRef(null);
  const [uploadedData, setUploadedData] = useState([]);
  const [parseError, setParseError] = useState(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setParseError(null);
    const fileName = file.name.toLowerCase();

    try {
      if (fileName.endsWith('.csv')) {
        const text = await file.text();
        const parsed = parseCSV(text);
        setUploadedData(parsed);
      } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        // For Excel, we'll use a simple approach - convert to CSV format
        setParseError('Excel files: Please save as CSV and re-upload. CSV format is more reliable.');
      } else {
        setParseError('Please upload a CSV file (.csv)');
      }
    } catch (err) {
      setParseError(`Error parsing file: ${err.message}`);
    }
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) throw new Error('CSV must have header row and at least one data row');

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const requiredFields = ['name', 'year', 'month', 'day', 'hour', 'minute', 'latitude', 'longitude'];
    
    const missing = requiredFields.filter(f => !headers.includes(f));
    if (missing.length > 0) {
      throw new Error(`Missing required columns: ${missing.join(', ')}`);
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length < headers.length) continue;

      const row = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx];
      });

      data.push({
        name: row.name || `Person ${i}`,
        year: parseInt(row.year) || 1990,
        month: parseInt(row.month) || 1,
        day: parseInt(row.day) || 1,
        hour: parseInt(row.hour) || 12,
        minute: parseInt(row.minute) || 0,
        latitude: parseFloat(row.latitude) || 28.6139,
        longitude: parseFloat(row.longitude) || 77.2090,
        timezone_offset: parseFloat(row.timezone_offset || row.timezone) || 5.5,
        place: row.place || 'India'
      });
    }

    return data;
  };

  const removeEntry = (index) => {
    setUploadedData(prev => prev.filter((_, i) => i !== index));
  };

  const downloadSampleCSV = () => {
    const sample = `name,year,month,day,hour,minute,latitude,longitude,timezone_offset,place
Rahul Sharma,1990,5,15,10,30,28.6139,77.2090,5.5,Delhi
Priya Singh,1985,8,22,14,45,19.0760,72.8777,5.5,Mumbai
Amit Kumar,1992,12,1,6,15,22.5726,88.3639,5.5,Kolkata`;
    
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_birth_details.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-amber-500/50 transition-all">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileUpload}
          accept=".csv"
          className="hidden"
          data-testid="file-input"
        />
        <FileSpreadsheet className="w-16 h-16 mx-auto text-gray-500 mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Upload CSV File</h3>
        <p className="text-gray-400 mb-4">
          Upload a CSV with columns: name, year, month, day, hour, minute, latitude, longitude, timezone_offset, place
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-all flex items-center gap-2 justify-center"
            data-testid="upload-csv-btn"
          >
            <Upload className="w-5 h-5" />
            Select CSV File
          </button>
          <button
            onClick={downloadSampleCSV}
            className="px-6 py-3 bg-gray-700 text-white font-semibold rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2 justify-center"
            data-testid="download-sample-btn"
          >
            <Download className="w-5 h-5" />
            Download Sample CSV
          </button>
        </div>
      </div>

      {parseError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
          <p className="text-red-400">{parseError}</p>
        </div>
      )}

      {/* Uploaded Data Preview */}
      {uploadedData.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-400" />
              {uploadedData.length} People Loaded
            </h3>
            <button
              onClick={() => setUploadedData([])}
              className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>
          </div>

          <div className="bg-gray-800/50 rounded-xl overflow-hidden">
            <div className="overflow-x-auto max-h-64">
              <table className="w-full text-sm">
                <thead className="bg-gray-900 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-gray-400">#</th>
                    <th className="px-4 py-3 text-left text-gray-400">Name</th>
                    <th className="px-4 py-3 text-left text-gray-400">Date</th>
                    <th className="px-4 py-3 text-left text-gray-400">Time</th>
                    <th className="px-4 py-3 text-left text-gray-400">Place</th>
                    <th className="px-4 py-3 text-left text-gray-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedData.map((person, idx) => (
                    <tr key={idx} className="border-t border-gray-700 hover:bg-gray-800/50">
                      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-white font-medium">{person.name}</td>
                      <td className="px-4 py-3 text-gray-300">{`${person.day}/${person.month}/${person.year}`}</td>
                      <td className="px-4 py-3 text-gray-300">{`${person.hour}:${String(person.minute).padStart(2, '0')}`}</td>
                      <td className="px-4 py-3 text-gray-300">{person.place}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => removeEntry(idx)}
                          className="text-red-400 hover:text-red-300 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Process Button */}
          <button
            onClick={() => onBulkProcess(uploadedData)}
            disabled={bulkLoading}
            className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-lg hover:from-green-400 hover:to-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="process-bulk-btn"
          >
            {bulkLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing {uploadedData.length} People...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Generate Kernels for All {uploadedData.length} People
              </>
            )}
          </button>
        </div>
      )}

      {/* Bulk Results */}
      {bulkResults.length > 0 && (
        <BulkResultsSection results={bulkResults} />
      )}
    </div>
  );
};

// Bulk Results Component
const BulkResultsSection = ({ results }) => {
  const downloadSingleKernel = (result) => {
    const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.name.replace(/\s+/g, '_')}_kernel.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllKernels = () => {
    // Create a combined JSON with all kernels
    const allData = results.filter(r => r.success).map(r => ({
      name: r.name,
      kernel: r.data
    }));
    
    const blob = new Blob([JSON.stringify(allData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `all_kernels_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const successCount = results.filter(r => r.success).length;

  return (
    <div className="space-y-4 mt-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-400" />
          {successCount} of {results.length} Kernels Generated
        </h3>
        {successCount > 0 && (
          <button
            onClick={downloadAllKernels}
            className="px-4 py-2 bg-amber-500 text-black font-semibold rounded-lg hover:bg-amber-400 transition-all flex items-center gap-2"
            data-testid="download-all-btn"
          >
            <Package className="w-4 h-4" />
            Download All ({successCount})
          </button>
        )}
      </div>

      <div className="bg-gray-800/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400">#</th>
                <th className="px-4 py-3 text-left text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-gray-400">Status</th>
                <th className="px-4 py-3 text-left text-gray-400">Lagna</th>
                <th className="px-4 py-3 text-left text-gray-400">Moon Sign</th>
                <th className="px-4 py-3 text-left text-gray-400">Nakshatra</th>
                <th className="px-4 py-3 text-left text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, idx) => (
                <tr key={idx} className="border-t border-gray-700 hover:bg-gray-800/50">
                  <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3 text-white font-medium">{result.name}</td>
                  <td className="px-4 py-3">
                    {result.success ? (
                      <span className="inline-flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-4 h-4" /> Success
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-400">
                        <AlertCircle className="w-4 h-4" /> Failed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-amber-400">
                    {result.data?.ascendant?.sign || '-'}
                  </td>
                  <td className="px-4 py-3 text-blue-400">
                    {result.data?.planets?.Moon?.sign || '-'}
                  </td>
                  <td className="px-4 py-3 text-purple-400">
                    {result.data?.nakshatra?.name || '-'}
                  </td>
                  <td className="px-4 py-3">
                    {result.success && (
                      <button
                        onClick={() => downloadSingleKernel(result)}
                        className="text-amber-400 hover:text-amber-300 transition-all flex items-center gap-1"
                      >
                        <Download className="w-4 h-4" />
                        JSON
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTENT DISPLAY COMPONENTS ====================

// Domain configuration for insights/reels
const domainList = [
  { key: 'health', label: 'Health', icon: Activity, color: 'text-red-400 bg-red-500/20' },
  { key: 'career', label: 'Career', icon: Target, color: 'text-blue-400 bg-blue-500/20' },
  { key: 'wealth', label: 'Wealth', icon: Sparkles, color: 'text-yellow-400 bg-yellow-500/20' },
  { key: 'marriage', label: 'Marriage', icon: Heart, color: 'text-pink-400 bg-pink-500/20' },
  { key: 'family', label: 'Family', icon: Users, color: 'text-purple-400 bg-purple-500/20' },
  { key: 'education', label: 'Education', icon: GraduationCap, color: 'text-green-400 bg-green-500/20' },
  { key: 'travel', label: 'Travel', icon: Plane, color: 'text-cyan-400 bg-cyan-500/20' },
  { key: 'spiritual', label: 'Spiritual', icon: Gem, color: 'text-amber-400 bg-amber-500/20' }
];

// ==================== AI-POWERED CONTENT COMPONENTS ====================

// Language selector component (reusable)
const LanguageSelector = ({ selectedLanguage, setSelectedLanguage }) => {
  const languages = [
    { key: 'hinglish', label: 'Hinglish' },
    { key: 'english', label: 'English' },
    { key: 'hindi', label: 'Hindi' }
  ];
  return (
    <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
      {languages.map(lang => (
        <button
          key={lang.key}
          onClick={() => setSelectedLanguage(lang.key)}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
            selectedLanguage === lang.key ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
          }`}
          data-testid={`lang-btn-${lang.key}`}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
};

// AI-Powered Past Story Content
const AiPastStoryContent = ({ storyData, loading, error, selectedLanguage, setSelectedLanguage, onRefresh, meta }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="past-story-loading">
        <Loader2 className="w-16 h-16 text-amber-500 animate-spin mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">AI Generating Your Past Story...</h3>
        <p className="text-gray-400 text-center max-w-md">
          TARAANAYA is analyzing your birth chart and Mahadasha timeline to create a deeply personalized life narrative.
        </p>
        <p className="text-amber-400 text-sm mt-4">This may take 30-60 seconds...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Failed to Generate Past Story</h3>
        <p className="text-gray-400 mb-4">{error}</p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium flex items-center gap-2"
          data-testid="retry-past-story"
        >
          <RefreshCw className="w-4 h-4" /> Try Again
        </button>
      </div>
    );
  }

  if (!storyData?.story) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Click a tab to generate AI-powered content.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-past-story-content">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">{meta?.name}'s Past Story</h3>
          <p className="text-gray-400 text-sm">
            AI-Generated • {storyData.word_count || 0} words • {storyData.language}
          </p>
        </div>
        <div className="flex gap-3">
          <LanguageSelector selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} />
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-600 flex items-center gap-1"
            data-testid="refresh-past-story"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Story Content */}
      <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-xl p-6 border border-purple-500/30">
        <div className="prose prose-invert max-w-none">
          <div className="text-gray-200 leading-relaxed whitespace-pre-wrap text-sm max-h-[700px] overflow-y-auto custom-scrollbar">
            {storyData.story}
          </div>
        </div>
      </div>
    </div>
  );
};

// AI-Powered Future Reels Content
const AiFutureReelsContent = ({ reels, loading, error, selectedLanguage, setSelectedLanguage, selectedDomain, setSelectedDomain, onFetchReel, meta }) => {
  const currentReel = reels[selectedDomain];
  const isLoading = loading[selectedDomain];
  const domainError = error[selectedDomain];

  return (
    <div className="space-y-6" data-testid="ai-future-reels-content">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">{meta?.name}'s Future Predictions</h3>
          <p className="text-gray-400 text-sm">AI-Generated • 8 Domains Available</p>
        </div>
        <div className="flex gap-3">
          <LanguageSelector selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} />
        </div>
      </div>

      {/* Domain Selector */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {domainList.map(domain => {
          const IconComp = domain.icon;
          const hasData = reels[domain.key];
          const isLoadingDomain = loading[domain.key];
          return (
            <button
              key={domain.key}
              onClick={() => setSelectedDomain(domain.key)}
              className={`flex flex-col items-center p-3 rounded-xl transition-all relative ${
                selectedDomain === domain.key 
                  ? 'bg-amber-500/20 border-2 border-amber-500' 
                  : 'bg-gray-800/50 border border-gray-700 hover:border-gray-600'
              }`}
              data-testid={`domain-btn-${domain.key}`}
            >
              {isLoadingDomain && (
                <div className="absolute inset-0 bg-gray-900/80 rounded-xl flex items-center justify-center">
                  <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                </div>
              )}
              <IconComp className={`w-6 h-6 ${selectedDomain === domain.key ? 'text-amber-400' : hasData ? 'text-green-400' : 'text-gray-400'}`} />
              <span className={`text-xs mt-1 ${selectedDomain === domain.key ? 'text-amber-400' : 'text-gray-500'}`}>{domain.label}</span>
              {hasData && <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full"></span>}
            </button>
          );
        })}
      </div>

      {/* Reel Content */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Generating {selectedDomain.charAt(0).toUpperCase() + selectedDomain.slice(1)} Future Reel...</h3>
            <p className="text-gray-400 text-sm">AI is analyzing your chart for this domain...</p>
          </div>
        ) : domainError ? (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
            <p className="text-gray-400 mb-4">{domainError}</p>
            <button
              onClick={() => onFetchReel(selectedDomain)}
              className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Try Again
            </button>
          </div>
        ) : currentReel?.reel ? (
          <div>
            <div className="flex items-center gap-3 mb-4">
              {domainList.find(d => d.key === selectedDomain) && (
                <>
                  {React.createElement(domainList.find(d => d.key === selectedDomain).icon, {
                    className: `w-8 h-8 ${domainList.find(d => d.key === selectedDomain).color.split(' ')[0]}`
                  })}
                  <div>
                    <h4 className="text-xl font-semibold text-white capitalize">{selectedDomain} Future</h4>
                    <p className="text-gray-400 text-xs">{currentReel.word_count || 0} words • {currentReel.language}</p>
                  </div>
                </>
              )}
              <button
                onClick={() => onFetchReel(selectedDomain)}
                className="ml-auto px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-600 flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>
            <div className="text-gray-200 leading-relaxed whitespace-pre-wrap text-sm max-h-[500px] overflow-y-auto custom-scrollbar">
              {currentReel.reel}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Play className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-4">Click to generate {selectedDomain} future predictions</p>
            <button
              onClick={() => onFetchReel(selectedDomain)}
              className="px-6 py-3 bg-amber-500 text-black rounded-lg font-semibold flex items-center gap-2 mx-auto"
              data-testid="generate-reel-btn"
            >
              <Sparkles className="w-5 h-5" /> Generate AI Prediction
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// AI-Powered Insights Content
const AiInsightsContent = ({ insightsData, loading, error, selectedLanguage, setSelectedLanguage, onRefresh, meta }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="insights-loading">
        <Loader2 className="w-16 h-16 text-amber-500 animate-spin mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">AI Generating Life Insights...</h3>
        <p className="text-gray-400 text-center max-w-md">
          Analyzing soul purpose, strengths, challenges, yogas, and life patterns from your chart.
        </p>
        <p className="text-amber-400 text-sm mt-4">This may take 30-60 seconds...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Failed to Generate Insights</h3>
        <p className="text-gray-400 mb-4">{error}</p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Try Again
        </button>
      </div>
    );
  }

  if (!insightsData?.insights) {
    return (
      <div className="text-center py-12">
        <Lightbulb className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">Insights will be generated when you view this tab.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-insights-content">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">Life Insights</h3>
          <p className="text-gray-400 text-sm">
            AI-Generated • {insightsData.word_count || 0} words • {insightsData.language}
          </p>
        </div>
        <div className="flex gap-3">
          <LanguageSelector selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} />
          <button
            onClick={onRefresh}
            className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-600 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Insights Content */}
      <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-xl p-6 border border-green-500/30">
        <div className="prose prose-invert max-w-none">
          <div className="text-gray-200 leading-relaxed whitespace-pre-wrap text-sm max-h-[700px] overflow-y-auto custom-scrollbar">
            {insightsData.insights}
          </div>
        </div>
      </div>
    </div>
  );
};

// AI-Powered Timelines Content
const AiTimelinesContent = ({ timelineData, loading, error, selectedLanguage, setSelectedLanguage, onFetchTimeline, meta, birthData }) => {
  const [timelineType, setTimelineType] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  const handleFetchTimeline = () => {
    if (timelineType === 'yearly') {
      onFetchTimeline('yearly', selectedYear);
    } else {
      onFetchTimeline('monthly', selectedYear, selectedMonth);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16" data-testid="timeline-loading">
        <Loader2 className="w-16 h-16 text-amber-500 animate-spin mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">AI Generating Timeline...</h3>
        <p className="text-gray-400 text-center max-w-md">
          Creating {timelineType === 'yearly' ? 'month-by-month' : 'week-by-week'} predictions using dasha and transit analysis.
        </p>
        <p className="text-amber-400 text-sm mt-4">This may take 30-60 seconds...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-timeline-content">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">Life Timelines</h3>
          <p className="text-gray-400 text-sm">{meta?.name} • AI-Powered Predictions</p>
        </div>
        <div className="flex gap-3">
          <LanguageSelector selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} />
        </div>
      </div>

      {/* Timeline Type Selector */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          {/* Type Toggle */}
          <div className="flex gap-1 bg-gray-900 rounded-lg p-1">
            <button
              onClick={() => setTimelineType('yearly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                timelineType === 'yearly' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              Yearly
            </button>
            <button
              onClick={() => setTimelineType('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                timelineType === 'monthly' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
          </div>

          {/* Year Selector */}
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
          >
            {[...Array(10)].map((_, i) => {
              const year = new Date().getFullYear() + i;
              return <option key={year} value={year}>{year}</option>;
            })}
          </select>

          {/* Month Selector (only for monthly) */}
          {timelineType === 'monthly' && (
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
            >
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                <option key={i} value={i + 1}>{m}</option>
              ))}
            </select>
          )}

          {/* Generate Button */}
          <button
            onClick={handleFetchTimeline}
            className="px-4 py-2 bg-amber-500 text-black rounded-lg font-medium flex items-center gap-2 hover:bg-amber-400"
            data-testid="generate-timeline-btn"
          >
            <Sparkles className="w-4 h-4" /> Generate
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-400" />
          <div>
            <p className="text-red-400 font-medium">Failed to generate timeline</p>
            <p className="text-gray-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Timeline Content */}
      {timelineData?.timeline ? (
        <div className="bg-gradient-to-r from-blue-900/20 to-indigo-900/20 rounded-xl p-6 border border-blue-500/30">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-semibold text-white">
                {timelineData.timeline_type === 'yearly' ? `${timelineData.period} Overview` : `${timelineData.period} Timeline`}
              </h4>
              <p className="text-gray-400 text-xs">{timelineData.word_count || 0} words • {timelineData.language}</p>
            </div>
            <button
              onClick={handleFetchTimeline}
              className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-600 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          <div className="prose prose-invert max-w-none">
            <div className="text-gray-200 leading-relaxed whitespace-pre-wrap text-sm max-h-[600px] overflow-y-auto custom-scrollbar">
              {timelineData.timeline}
            </div>
          </div>
        </div>
      ) : !loading && (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
          <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">Select timeline type and period, then click Generate.</p>
        </div>
      )}
    </div>
  );
};

// Past Story Content - Shows 3 languages + Mahadasha-wise detailed content
const PastStoryContent = ({ storyData, selectedLanguage, setSelectedLanguage, meta }) => {
  const [expandedMD, setExpandedMD] = useState(null);
  const [viewMode, setViewMode] = useState('forensic'); // 'forensic' or 'narrative'
  const [expandedDomain, setExpandedDomain] = useState(null);

  if (!storyData?.past_story) {
    return (
      <div className="text-center py-12">
        <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No past story data available. Generate kernel first.</p>
      </div>
    );
  }

  const pastStory = storyData.past_story;
  const forensic = pastStory.forensic?.[selectedLanguage] || pastStory.forensic?.hinglish;
  const intro = pastStory.intro || {};
  const pastMahadashas = pastStory.mahadashas || [];
  const languages = [
    { key: 'hinglish', label: 'Hinglish' },
    { key: 'english', label: 'English' },
    { key: 'hindi', label: 'हिंदी' }
  ];

  const domainEmojis = {
    health: '🏥',
    career: '💼',
    wealth: '💰',
    marriage: '💍',
    family: '👨‍👩‍👧',
    education: '📚',
    spiritual: '🕉️',
    travel: '✈️'
  };

  const domainColors = {
    health: 'from-red-500/20 to-red-900/20 border-red-500/30',
    career: 'from-blue-500/20 to-blue-900/20 border-blue-500/30',
    wealth: 'from-green-500/20 to-green-900/20 border-green-500/30',
    marriage: 'from-pink-500/20 to-pink-900/20 border-pink-500/30',
    family: 'from-purple-500/20 to-purple-900/20 border-purple-500/30',
    education: 'from-indigo-500/20 to-indigo-900/20 border-indigo-500/30',
    spiritual: 'from-amber-500/20 to-amber-900/20 border-amber-500/30',
    travel: 'from-cyan-500/20 to-cyan-900/20 border-cyan-500/30'
  };

  return (
    <div className="space-y-6">
      {/* Header with language & view mode selector */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">{meta?.name}'s Past Story</h3>
          <p className="text-gray-400 text-sm">Forensic Life Audit • Chart-Based Analysis</p>
        </div>
        <div className="flex gap-4">
          {/* View Mode Toggle */}
          <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('forensic')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'forensic' ? 'bg-green-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              Forensic
            </button>
            <button
              onClick={() => setViewMode('narrative')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                viewMode === 'narrative' ? 'bg-purple-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              Narrative
            </button>
          </div>
          {/* Language Selector */}
          <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
            {languages.map(lang => (
              <button
                key={lang.key}
                onClick={() => setSelectedLanguage(lang.key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  selectedLanguage === lang.key ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
                }`}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* FORENSIC VIEW - Chart-Audit Style */}
      {viewMode === 'forensic' && forensic && (
        <div className="space-y-6">
          {/* SECTION A: 8-Domain Structural Diagnostic */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl border border-amber-500/30 overflow-hidden">
            <div className="bg-amber-500/10 px-6 py-3 border-b border-amber-500/30">
              <h4 className="text-lg font-bold text-amber-400">SECTION A: 8-Domain Structural Diagnostic</h4>
              <p className="text-gray-400 text-xs">Chart-based analysis (NOT timeline) • Click domain to expand</p>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              {forensic.section_a_diagnostic && Object.entries(forensic.section_a_diagnostic).map(([domain, content]) => (
                <div 
                  key={domain}
                  className={`rounded-lg border cursor-pointer transition-all hover:scale-[1.02] bg-gradient-to-r ${domainColors[domain] || 'from-gray-700/50 to-gray-800/50 border-gray-600'} ${
                    expandedDomain === domain ? 'ring-2 ring-amber-500' : ''
                  }`}
                  onClick={() => setExpandedDomain(expandedDomain === domain ? null : domain)}
                >
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{domainEmojis[domain] || '📊'}</span>
                      <span className="text-white font-semibold uppercase text-sm">{domain}</span>
                    </div>
                    <div className={`text-gray-300 text-xs leading-relaxed whitespace-pre-wrap ${
                      expandedDomain === domain ? '' : 'line-clamp-3'
                    }`}>
                      {content || 'No data available'}
                    </div>
                    {content && content.length > 150 && (
                      <p className="text-amber-400 text-xs mt-2">
                        {expandedDomain === domain ? '▲ Click to collapse' : '▼ Click to expand'}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION B: Mahadasha Event Replay */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl border border-purple-500/30 overflow-hidden">
            <div className="bg-purple-500/10 px-6 py-3 border-b border-purple-500/30">
              <h4 className="text-lg font-bold text-purple-400">SECTION B: Mahadasha Event Replay</h4>
              <p className="text-gray-400 text-xs">Quality events only • No generic filler</p>
            </div>
            <div className="p-4 space-y-3">
              {forensic.section_b_mahadasha_events && forensic.section_b_mahadasha_events.map((md, idx) => (
                <div 
                  key={idx}
                  className={`rounded-lg border border-gray-700 overflow-hidden ${
                    md.is_current ? 'border-amber-500/50 bg-amber-500/5' : 'bg-gray-800/30'
                  }`}
                >
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-700/30 transition-all flex items-center justify-between"
                    onClick={() => setExpandedMD(expandedMD === idx ? null : idx)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                        md.is_current ? 'bg-amber-500 text-black' : 'bg-gray-700 text-white'
                      }`}>
                        {md.lord?.[0] || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold">{md.lord} Mahadasha</span>
                          {md.is_current && <span className="px-2 py-0.5 bg-amber-500 text-black rounded text-xs font-bold">CURRENT</span>}
                        </div>
                        <p className="text-gray-400 text-xs">{md.years} • Age {md.age_range}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        md.total_events > 5 ? 'bg-green-500/20 text-green-400' :
                        md.total_events > 2 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-gray-600/50 text-gray-400'
                      }`}>
                        {md.total_events || 0} events
                      </span>
                      <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedMD === idx ? 'rotate-90' : ''}`} />
                    </div>
                  </div>

                  {/* Expanded Quality Events */}
                  {expandedMD === idx && (
                    <div className="border-t border-gray-700 p-4 bg-gray-900/50">
                      {md.quality_events && Object.keys(md.quality_events).length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(md.quality_events).map(([domain, events]) => (
                            events && events.length > 0 && (
                              <div key={domain} className="bg-gray-800/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span>{domainEmojis[domain] || '📊'}</span>
                                  <span className="text-white font-medium uppercase text-xs">{domain}</span>
                                </div>
                                <ul className="space-y-1">
                                  {events.map((event, eIdx) => (
                                    <li key={eIdx} className="text-gray-300 text-xs flex items-start gap-2">
                                      <span className="text-amber-400">•</span>
                                      <span>{event}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm italic">Stable period - no major events recorded</p>
                      )}
                      {md.summary && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <p className="text-gray-400 text-xs"><span className="text-amber-400 font-medium">Summary:</span> {md.summary}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* SECTION C: Transition */}
          {forensic.section_c_transition && (
            <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-xl p-6 border border-green-500/30">
              <h4 className="text-lg font-semibold text-green-400 mb-3">SECTION C: Transition to Present</h4>
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{forensic.section_c_transition}</p>
            </div>
          )}
        </div>
      )}

      {/* NARRATIVE VIEW - Shows Full Forensic Story as text */}
      {viewMode === 'narrative' && (
        <div className="space-y-6">
          {/* Full Story Text */}
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/30">
            <h4 className="text-lg font-semibold text-purple-400 mb-4">
              {selectedLanguage === 'hindi' ? 'पूर्ण जीवन कथा' : 
               selectedLanguage === 'english' ? 'Full Life Story' : 
               'Poori Kahani'}
            </h4>
            <div className="text-gray-300 leading-relaxed whitespace-pre-wrap text-sm max-h-[600px] overflow-y-auto custom-scrollbar">
              {/* Use forensic full_story if available, else fallback to intro */}
              {forensic?.full_story || intro[selectedLanguage] || intro.hinglish || 'Story content not available.'}
            </div>
          </div>
        </div>
      )}

      {/* Fallback if no forensic data */}
      {viewMode === 'forensic' && !forensic && (
        <div className="bg-gray-800/50 rounded-xl p-8 text-center">
          <p className="text-gray-400">Forensic analysis not available. Showing narrative view instead.</p>
          <button 
            onClick={() => setViewMode('narrative')}
            className="mt-4 px-4 py-2 bg-amber-500 text-black rounded-lg font-medium"
          >
            Switch to Narrative View
          </button>
        </div>
      )}
    </div>
  );
};

// Future Reels Content - Shows 8 domains × 3 languages with Mahadasha-wise predictions
const FutureReelsContent = ({ storyData, selectedLanguage, setSelectedLanguage, selectedDomain, setSelectedDomain, meta }) => {
  const [selectedMD, setSelectedMD] = useState(0);

  if (!storyData?.future_story) {
    return (
      <div className="text-center py-12">
        <Play className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No future predictions available. Generate kernel first.</p>
      </div>
    );
  }

  const futureStory = storyData.future_story;
  const domainReels = futureStory.domain_reels || {};
  const futureMahadashas = futureStory.mahadashas || [];
  const languages = [
    { key: 'hinglish', label: 'Hinglish' },
    { key: 'english', label: 'English' },
    { key: 'hindi', label: 'हिंदी' }
  ];

  // Get content for selected language and domain
  const langReels = domainReels[selectedLanguage] || domainReels.hinglish || {};
  const currentContent = langReels[selectedDomain] || '';
  const currentMD = futureMahadashas[selectedMD] || null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">{meta?.name}'s Future Predictions</h3>
          <p className="text-gray-400 text-sm">{futureMahadashas.length} Future Mahadashas • 8 Domains × 3 Languages</p>
        </div>
        <div className="flex gap-2 bg-gray-800 rounded-lg p-1">
          {languages.map(lang => (
            <button
              key={lang.key}
              onClick={() => setSelectedLanguage(lang.key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                selectedLanguage === lang.key ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>

      {/* Mahadasha Selector (Horizontal Scroll) */}
      {futureMahadashas.length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <h4 className="text-sm text-gray-400 mb-3">Select Mahadasha Period</h4>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {futureMahadashas.filter(md => md.status === 'current' || md.status === 'future').slice(0, 8).map((md, idx) => (
              <button
                key={idx}
                onClick={() => setSelectedMD(idx)}
                className={`flex-shrink-0 px-4 py-3 rounded-xl transition-all ${
                  selectedMD === idx
                    ? 'bg-amber-500 text-black'
                    : md.status === 'current' ? 'bg-amber-500/20 border border-amber-500' : 'bg-gray-700 text-gray-300'
                }`}
              >
                <div className="text-center">
                  <div className="text-lg font-bold">{md.icon || md.lord?.charAt(0)}</div>
                  <div className="text-xs font-medium">{md.lord}</div>
                  <div className="text-xs opacity-75">{md.years}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Domain Selector */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {domainList.map(domain => {
          const IconComp = domain.icon;
          return (
            <button
              key={domain.key}
              onClick={() => setSelectedDomain(domain.key)}
              className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                selectedDomain === domain.key 
                  ? 'bg-amber-500/20 border-2 border-amber-500' 
                  : 'bg-gray-800/50 border border-gray-700 hover:border-gray-600'
              }`}
            >
              <IconComp className={`w-6 h-6 ${selectedDomain === domain.key ? 'text-amber-400' : 'text-gray-400'}`} />
              <span className={`text-xs mt-1 ${selectedDomain === domain.key ? 'text-amber-400' : 'text-gray-500'}`}>{domain.label}</span>
            </button>
          );
        })}
      </div>

      {/* Domain Reel Content (Full Script) */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          {domainList.find(d => d.key === selectedDomain) && (
            <>
              {React.createElement(domainList.find(d => d.key === selectedDomain).icon, {
                className: `w-8 h-8 ${domainList.find(d => d.key === selectedDomain).color.split(' ')[0]}`
              })}
              <h4 className="text-xl font-semibold text-white capitalize">{selectedDomain} Future ({selectedLanguage})</h4>
            </>
          )}
        </div>
        <div className="text-gray-300 leading-relaxed whitespace-pre-wrap text-sm max-h-80 overflow-y-auto">
          {currentContent || `No ${selectedDomain} predictions available for ${selectedLanguage}.`}
        </div>
      </div>

      {/* Mahadasha Domain Predictions (Detailed) */}
      {currentMD && currentMD.domain_predictions && (
        <div className="bg-gray-800/30 rounded-xl p-6 border border-gray-700">
          <h4 className="text-lg font-semibold text-amber-400 mb-4">
            {currentMD.lord} Mahadasha ({currentMD.years}) - Domain Predictions
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(currentMD.domain_predictions).map(([domain, pred]) => {
              const domainInfo = domainList.find(d => d.key === domain);
              return (
                <div key={domain} className={`rounded-lg p-4 ${selectedDomain === domain ? 'bg-amber-500/20 border border-amber-500' : 'bg-gray-800/50'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {domainInfo && React.createElement(domainInfo.icon, { className: `w-5 h-5 ${domainInfo.color.split(' ')[0]}` })}
                    <span className="text-white font-medium capitalize">{domain}</span>
                  </div>
                  <p className="text-gray-300 text-sm">{pred.summary}</p>
                  {pred.hinglish_summary && (
                    <p className="text-amber-400 text-xs mt-2 italic">{pred.hinglish_summary}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Insights Content - Shows domain insights from unified-story
const InsightsContent = ({ storyData, selectedDomain, setSelectedDomain, meta }) => {
  if (!storyData?.domain_insights) {
    return (
      <div className="text-center py-12">
        <Lightbulb className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No insights available. Generate kernel first.</p>
      </div>
    );
  }

  const insights = storyData.domain_insights || {};
  const currentInsight = insights[selectedDomain] || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-amber-400">Life Insights</h3>
        <p className="text-gray-400 text-sm">{meta?.name} • 8 Domain Insights</p>
      </div>

      {/* Domain Selector */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {domainList.map(domain => {
          const IconComp = domain.icon;
          return (
            <button
              key={domain.key}
              onClick={() => setSelectedDomain(domain.key)}
              className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                selectedDomain === domain.key 
                  ? 'bg-amber-500/20 border-2 border-amber-500' 
                  : 'bg-gray-800/50 border border-gray-700 hover:border-gray-600'
              }`}
            >
              <IconComp className={`w-6 h-6 ${selectedDomain === domain.key ? 'text-amber-400' : 'text-gray-400'}`} />
              <span className={`text-xs mt-1 ${selectedDomain === domain.key ? 'text-amber-400' : 'text-gray-500'}`}>{domain.label}</span>
            </button>
          );
        })}
      </div>

      {/* Domain Insights Content */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          {domainList.find(d => d.key === selectedDomain) && (
            <>
              {React.createElement(domainList.find(d => d.key === selectedDomain).icon, {
                className: `w-8 h-8 ${domainList.find(d => d.key === selectedDomain).color.split(' ')[0]}`
              })}
              <h4 className="text-xl font-semibold text-white capitalize">{selectedDomain} Insights</h4>
            </>
          )}
        </div>

        {/* Render all insight sections */}
        <div className="space-y-4 max-h-[500px] overflow-y-auto">
          {Object.entries(currentInsight).map(([key, value]) => {
            if (!value) return null;
            const displayKey = key.replace(/_/g, ' ').replace(/sub \d+/i, '').trim();
            
            return (
              <div key={key} className="bg-gray-900/50 rounded-lg p-4">
                <h5 className="text-sm font-medium text-amber-400 capitalize mb-2">{displayKey}</h5>
                {typeof value === 'string' ? (
                  <p className="text-gray-300 text-sm leading-relaxed">{value}</p>
                ) : typeof value === 'object' ? (
                  <div className="text-gray-300 text-sm">
                    {Object.entries(value).map(([k, v]) => (
                      <div key={k} className="mb-2">
                        <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}: </span>
                        <span>{typeof v === 'object' ? JSON.stringify(v) : v}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-300 text-sm">{String(value)}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Timelines Content - Shows monthly/yearly from /api/timeline APIs
const TimelinesContent = ({ timelineData, meta, birthData }) => {
  const [timelineView, setTimelineView] = useState('yearly');
  const [yearlyReport, setYearlyReport] = useState(null);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [selectedMonth, setSelectedMonth] = useState(3);
  const [selectedDay, setSelectedDay] = useState(null); // For showing daily detail popup

  // Fetch detailed yearly report
  useEffect(() => {
    if (timelineView === 'yearly' && birthData && !yearlyReport) {
      fetchYearlyReport();
    }
  }, [timelineView, birthData, selectedYear]);

  // Fetch detailed monthly report
  useEffect(() => {
    if (timelineView === 'monthly' && birthData) {
      fetchMonthlyReport();
    }
  }, [timelineView, birthData, selectedMonth, selectedYear]);

  const fetchYearlyReport = async () => {
    if (!birthData) return;
    setYearlyLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/timeline/yearly-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: birthData.name,
          year: birthData.year,
          month: birthData.month,
          day: birthData.day,
          hour: birthData.hour,
          minute: birthData.minute,
          latitude: birthData.latitude,
          longitude: birthData.longitude,
          place: birthData.place || 'Unknown',
          gender: birthData.gender || 'unknown',
          target_year: selectedYear
        })
      });
      const result = await response.json();
      if (result.success && result.data) {
        setYearlyReport(result.data);
      }
    } catch (err) {
      console.error('Error fetching yearly report:', err);
    } finally {
      setYearlyLoading(false);
    }
  };

  const fetchMonthlyReport = async () => {
    if (!birthData) return;
    setMonthlyLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/timeline/monthly-detailed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: birthData.name,
          year: birthData.year,
          month: birthData.month,
          day: birthData.day,
          hour: birthData.hour,
          minute: birthData.minute,
          latitude: birthData.latitude,
          longitude: birthData.longitude,
          place: birthData.place || 'Unknown',
          gender: birthData.gender || 'unknown',
          target_month: selectedMonth,
          target_year: selectedYear
        })
      });
      const result = await response.json();
      if (result.success && result.data) {
        setMonthlyReport(result.data);
      }
    } catch (err) {
      console.error('Error fetching monthly report:', err);
    } finally {
      setMonthlyLoading(false);
    }
  };

  // Parse full_report into sections
  const parseFullReport = (report) => {
    if (!report) return {};
    const sections = {};
    const domainPatterns = [
      { key: 'year_theme', pattern: /YEAR THEME\s*([\s\S]*?)(?=HEALTH|$)/i },
      { key: 'health', pattern: /HEALTH\s*([\s\S]*?)(?=CAREER|$)/i },
      { key: 'career', pattern: /CAREER\s*([\s\S]*?)(?=WEALTH|$)/i },
      { key: 'wealth', pattern: /WEALTH\s*([\s\S]*?)(?=EDUCATION|$)/i },
      { key: 'education', pattern: /EDUCATION\s*([\s\S]*?)(?=FAMILY|$)/i },
      { key: 'family', pattern: /FAMILY\s*([\s\S]*?)(?=RELATIONSHIPS|$)/i },
      { key: 'relationships', pattern: /RELATIONSHIPS\s*([\s\S]*?)(?=TRAVEL|$)/i },
      { key: 'travel', pattern: /TRAVEL\s*([\s\S]*?)(?=SPIRITUALITY|$)/i },
      { key: 'spirituality', pattern: /SPIRITUALITY\s*([\s\S]*?)$/i }
    ];
    
    for (const { key, pattern } of domainPatterns) {
      const match = report.match(pattern);
      if (match) {
        sections[key] = match[1].trim();
      }
    }
    return sections;
  };

  if (!timelineData && !yearlyReport) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400">No timeline data available. Generate kernel first.</p>
      </div>
    );
  }

  const monthlyPreds = timelineData?.monthly_predictions || {};
  const parsedReport = parseFullReport(yearlyReport?.full_report || '');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-amber-400">Life Timelines</h3>
          <p className="text-gray-400 text-sm">{meta?.name} • Monthly & Yearly Predictions</p>
        </div>
        <div className="flex gap-2 bg-gray-800 rounded-lg p-1">
          {['yearly', 'monthly', 'dasha'].map(view => (
            <button
              key={view}
              onClick={() => setTimelineView(view)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all capitalize ${
                timelineView === view ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
              }`}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      {/* Yearly View - Detailed Report */}
      {timelineView === 'yearly' && (
        <div className="space-y-4">
          {yearlyLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mb-4" />
              <p className="text-gray-400">Loading {selectedYear} predictions...</p>
            </div>
          ) : (
            <>
              {/* Year Theme Header */}
              <div className="bg-gradient-to-r from-amber-900/30 to-red-900/30 rounded-xl p-6 border border-amber-500/30">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-2xl font-bold text-white mb-2">Year {selectedYear}</h4>
                    {yearlyReport?.dasha && (
                      <p className="text-amber-400">
                        {yearlyReport.dasha.mahadasha} - {yearlyReport.dasha.antardasha} Dasha
                      </p>
                    )}
                    {yearlyReport?.dasha?.next_mahadasha && (
                      <p className="text-gray-500 text-sm mt-1">
                        Next: {yearlyReport.dasha.next_mahadasha} from {yearlyReport.dasha.next_mahadasha_start}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {[2025, 2026, 2027, 2028].map(y => (
                      <button
                        key={y}
                        onClick={() => { setSelectedYear(y); setYearlyReport(null); }}
                        className={`px-3 py-1 rounded-lg text-sm ${
                          selectedYear === y ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300'
                        }`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                </div>
                {parsedReport.year_theme && (
                  <p className="text-gray-300 mt-4 leading-relaxed">{parsedReport.year_theme}</p>
                )}
              </div>

              {/* Domain Predictions - Full Text */}
              <div className="space-y-4">
                {['health', 'career', 'wealth', 'education', 'family', 'relationships', 'travel', 'spirituality'].map(domain => {
                  const content = parsedReport[domain];
                  if (!content) return null;
                  const domainInfo = domainList.find(d => d.key === domain || d.key === domain.toLowerCase());
                  return (
                    <div key={domain} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                      <div className="flex items-center gap-3 mb-4">
                        {domainInfo ? (
                          React.createElement(domainInfo.icon, { className: `w-6 h-6 ${domainInfo.color.split(' ')[0]}` })
                        ) : (
                          <Sparkles className="w-6 h-6 text-amber-400" />
                        )}
                        <h5 className="text-lg font-semibold text-white capitalize">{domain}</h5>
                      </div>
                      <p className="text-gray-300 leading-relaxed text-sm whitespace-pre-wrap">{content}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Monthly View */}
      {timelineView === 'monthly' && (
        <div className="space-y-4">
          {monthlyLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mb-4" />
              <p className="text-gray-400">Loading monthly predictions...</p>
            </div>
          ) : (
            <>
              {/* Month Header with Month Selector */}
              <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/30">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-xl font-bold text-white mb-2">
                      {monthlyReport?.month_name || ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][selectedMonth-1]} {selectedYear}
                    </h4>
                    {monthlyReport?.dasha && (
                      <p className="text-purple-400">
                        {monthlyReport.dasha.mahadasha} - {monthlyReport.dasha.antardasha} Dasha
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                      <button
                        key={m}
                        onClick={() => { setSelectedMonth(m); setMonthlyReport(null); }}
                        className={`px-2 py-1 rounded text-xs ${
                          selectedMonth === m ? 'bg-purple-500 text-white' : 'bg-gray-700 text-gray-300'
                        }`}
                      >
                        {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Monthly Overview */}
              {monthlyReport?.monthly_overview && (
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <h5 className="text-lg font-semibold text-amber-400 mb-4">Monthly Overview</h5>
                  <p className="text-gray-300 leading-relaxed text-sm whitespace-pre-wrap">
                    {monthlyReport.monthly_overview}
                  </p>
                </div>
              )}

              {/* Key Days */}
              {monthlyReport?.key_days && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Favorable Days */}
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
                    <h5 className="text-green-400 font-semibold mb-3">✓ Favorable Days</h5>
                    <div className="space-y-2">
                      {(monthlyReport.key_days.favorable || []).map((day, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 font-bold">{day.day}</span>
                          <span className="text-gray-300 text-sm">{day.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Challenging Days */}
                  <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
                    <h5 className="text-red-400 font-semibold mb-3">✗ Challenging Days</h5>
                    <div className="space-y-2">
                      {(monthlyReport.key_days.challenging || []).map((day, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center text-red-400 font-bold">{day.day}</span>
                          <span className="text-gray-300 text-sm">{day.reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Daily Details Calendar - FIXED: daily_details is an ARRAY */}
              {monthlyReport?.daily_details && Array.isArray(monthlyReport.daily_details) && monthlyReport.daily_details.length > 0 && (
                <div className="bg-gray-800/30 rounded-xl p-6 border border-gray-700">
                  <h5 className="text-lg font-semibold text-amber-400 mb-4">Daily Breakdown (Click any day for details)</h5>
                  <div className="grid grid-cols-7 gap-2">
                    {monthlyReport.daily_details.map((dayData, idx) => {
                      const dayNum = dayData.day || idx + 1;
                      const score = dayData.quality_score || 5;
                      const quality = dayData.quality || 'neutral';
                      const isSelected = selectedDay === dayNum;
                      const isHighlyFavorable = quality === 'highly_favorable' || quality === 'favorable';
                      const isChallenging = quality === 'challenging' || quality === 'difficult';
                      
                      return (
                        <div 
                          key={dayNum} 
                          onClick={() => setSelectedDay(isSelected ? null : dayNum)}
                          className={`p-2 rounded-lg text-center cursor-pointer transition-all hover:scale-105 ${
                            isSelected ? 'ring-2 ring-amber-500 scale-105' : ''
                          } ${
                            isHighlyFavorable ? 'bg-green-500/20 border border-green-500/30' :
                            isChallenging ? 'bg-red-500/20 border border-red-500/30' :
                            'bg-yellow-500/20 border border-yellow-500/30'
                          }`}
                        >
                          <p className="text-white font-bold text-sm">{dayNum}</p>
                          <p className={`text-xs font-medium ${
                            isHighlyFavorable ? 'text-green-400' : isChallenging ? 'text-red-400' : 'text-yellow-400'
                          }`}>{dayData.moon_transit?.sign?.slice(0,3) || ''}</p>
                          <p className="text-gray-500 text-xs">{dayData.dasha_active?.sd || ''}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Selected Day Detail Panel */}
              {selectedDay && monthlyReport?.daily_details && (
                <div className="bg-gradient-to-br from-amber-900/20 to-purple-900/20 rounded-xl p-6 border border-amber-500/30">
                  {(() => {
                    const dayData = monthlyReport.daily_details.find(d => d.day === selectedDay) || monthlyReport.daily_details[selectedDay - 1];
                    if (!dayData) return <p className="text-gray-400">No data for day {selectedDay}</p>;
                    
                    return (
                      <>
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h5 className="text-xl font-bold text-white">
                              {dayData.weekday || ''}, {monthlyReport.month_name} {selectedDay}
                            </h5>
                            <p className="text-amber-400 text-sm">
                              {dayData.dasha_active?.md}-{dayData.dasha_active?.ad}-{dayData.dasha_active?.pd}-{dayData.dasha_active?.sd} Dasha
                            </p>
                          </div>
                          <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                            dayData.quality === 'highly_favorable' ? 'bg-green-500 text-black' :
                            dayData.quality === 'favorable' ? 'bg-green-400 text-black' :
                            dayData.quality === 'challenging' ? 'bg-red-500 text-white' :
                            dayData.quality === 'difficult' ? 'bg-red-600 text-white' :
                            'bg-yellow-500 text-black'
                          }`}>
                            {dayData.quality?.replace('_', ' ').toUpperCase() || 'NEUTRAL'}
                          </div>
                        </div>
                        
                        {/* Moon Transit Info */}
                        <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
                          <p className="text-purple-400 text-sm">
                            <span className="font-semibold">Moon:</span> {dayData.moon_transit?.sign} ({dayData.moon_transit?.nakshatra}) • House {dayData.moon_transit?.house_from_lagna} from Lagna • {dayData.moon_transit?.house_quality}
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            Day Ruler: {dayData.day_ruler} | Quality Score: {dayData.quality_score}/100
                          </p>
                        </div>

                        {/* Domain Events - THE MAIN CONTENT USER WANTS */}
                        <h6 className="text-amber-400 font-semibold mb-3">Domain Predictions for This Day</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {dayData.domain_events && Object.entries(dayData.domain_events).map(([domain, eventData]) => {
                            const domainInfo = domainList.find(d => d.key === domain);
                            if (!eventData) return null;
                            
                            return (
                              <div key={domain} className={`rounded-lg p-3 ${domainInfo?.color?.split(' ')[1] || 'bg-gray-800/50'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  {domainInfo && React.createElement(domainInfo.icon, { className: `w-4 h-4 ${domainInfo.color.split(' ')[0]}` })}
                                  <span className="text-white font-medium capitalize text-sm">{domain}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    eventData.quality === 'highly_favorable' || eventData.quality === 'favorable' 
                                      ? 'bg-green-500/30 text-green-400' 
                                      : eventData.quality === 'challenging' || eventData.quality === 'difficult'
                                      ? 'bg-red-500/30 text-red-400'
                                      : 'bg-gray-500/30 text-gray-400'
                                  }`}>{eventData.quality || 'neutral'}</span>
                                </div>
                                <p className="text-gray-300 text-sm leading-relaxed">{eventData.text}</p>
                                {eventData.all_events && eventData.all_events.length > 1 && (
                                  <div className="mt-2 pt-2 border-t border-gray-700/50">
                                    <p className="text-gray-400 text-xs italic">
                                      Also: {eventData.all_events.slice(1).join(' | ')}
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Close button */}
                        <button 
                          onClick={() => setSelectedDay(null)}
                          className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all text-sm"
                        >
                          Close Day Details
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Dasha View */}
      {timelineView === 'dasha' && (
        <div className="space-y-4">
          <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
            <h4 className="text-lg font-semibold text-amber-400 mb-4">Current Dasha Hierarchy</h4>
            {timelineData?.current_hierarchy && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(timelineData.current_hierarchy).filter(([k]) => k !== 'current_age').map(([level, data]) => {
                  // Extract lord name from nested object
                  const lordName = data?.lord || (typeof data === 'string' ? data : '');
                  const extraInfo = data?.remaining_years || data?.remaining_months || data?.remaining_days || '';
                  return (
                    <div key={level} className="bg-gray-900/50 rounded-lg p-4 text-center">
                      <p className="text-gray-500 text-xs capitalize mb-1">{level.replace(/_/g, ' ')}</p>
                      <p className="text-white font-bold text-xl">{lordName}</p>
                      {extraInfo && (
                        <p className="text-amber-400 text-xs mt-1">{extraInfo} {data?.remaining_years ? 'yrs' : data?.remaining_months ? 'mo' : 'days'} left</p>
                      )}
                    </div>
                  );
                })}
                {timelineData.current_hierarchy.current_age && (
                  <div className="bg-amber-500/20 rounded-lg p-4 text-center border border-amber-500">
                    <p className="text-gray-500 text-xs mb-1">Current Age</p>
                    <p className="text-amber-400 font-bold text-xl">{Math.round(timelineData.current_hierarchy.current_age * 10) / 10}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Full Dasha Breakdown */}
          {timelineData?.full_dasha_breakdown && Array.isArray(timelineData.full_dasha_breakdown) && (
            <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
              <h4 className="text-lg font-semibold text-amber-400 mb-4">Mahadasha Timeline</h4>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {timelineData.full_dasha_breakdown.map((md, idx) => (
                  <div key={idx} className={`flex items-center gap-4 p-4 rounded-lg border ${
                    md.status === 'current' ? 'bg-amber-500/20 border-amber-500' : 'bg-gray-900/50 border-gray-700'
                  }`}>
                    <div className={`w-4 h-4 rounded-full ${md.status === 'current' ? 'bg-amber-500' : md.status === 'completed' ? 'bg-gray-500' : 'bg-blue-500'}`} />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-bold">{md.lord} Mahadasha</span>
                        <span className="text-gray-400 text-sm">{md.years} years</span>
                      </div>
                      <p className="text-gray-400 text-sm">{md.start_date} → {md.end_date}</p>
                    </div>
                    {md.status === 'current' && <span className="px-3 py-1 bg-amber-500 text-black rounded-full text-xs font-bold">CURRENT</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Kernel Display Tabs Component
const KernelDisplayTabs = ({ kernelData, onDownload, birthData }) => {
  const [activeTab, setActiveTab] = useState('domains');
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('hinglish');
  const [selectedDomain, setSelectedDomain] = useState('health');

  // AI-Powered Story States (Individual loading for each section)
  const [aiPastStory, setAiPastStory] = useState(null);
  const [pastStoryLoading, setPastStoryLoading] = useState(false);
  const [pastStoryError, setPastStoryError] = useState(null);

  const [aiFutureReels, setAiFutureReels] = useState({});
  const [futureReelLoading, setFutureReelLoading] = useState({});
  const [futureReelError, setFutureReelError] = useState({});

  const [aiInsights, setAiInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState(null);

  const [aiTimeline, setAiTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  // Build request payload for AI story APIs
  const buildStoryRequest = () => ({
    birth_data: {
      name: birthData.name,
      year: birthData.year,
      month: birthData.month,
      day: birthData.day,
      hour: birthData.hour,
      minute: birthData.minute,
      latitude: birthData.latitude,
      longitude: birthData.longitude,
      gender: birthData.gender || 'male'
    },
    past_mahadashas: [],
    current_mahadasha: {},
    future_mahadashas: [],
    language: selectedLanguage
  });

  // Fetch AI-Powered Past Story
  const fetchAiPastStory = async () => {
    if (!birthData || pastStoryLoading) return;
    setPastStoryLoading(true);
    setPastStoryError(null);
    try {
      const response = await fetch(`${API_URL}/api/ai-story/past-story`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildStoryRequest(),
          language: selectedLanguage
        })
      });
      const result = await response.json();
      if (result.success) {
        setAiPastStory(result);
      } else {
        setPastStoryError(result.error || 'Failed to generate past story');
      }
    } catch (err) {
      console.error('Error fetching AI past story:', err);
      setPastStoryError(err.message);
    } finally {
      setPastStoryLoading(false);
    }
  };

  // Fetch AI-Powered Future Reel for specific domain
  const fetchAiFutureReel = async (domain) => {
    if (!birthData || futureReelLoading[domain]) return;
    setFutureReelLoading(prev => ({ ...prev, [domain]: true }));
    setFutureReelError(prev => ({ ...prev, [domain]: null }));
    try {
      const response = await fetch(`${API_URL}/api/ai-story/future-reel/${domain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildStoryRequest(),
          language: selectedLanguage
        })
      });
      const result = await response.json();
      if (result.success) {
        setAiFutureReels(prev => ({ ...prev, [domain]: result }));
      } else {
        setFutureReelError(prev => ({ ...prev, [domain]: result.error || 'Failed to generate future reel' }));
      }
    } catch (err) {
      console.error(`Error fetching AI future reel for ${domain}:`, err);
      setFutureReelError(prev => ({ ...prev, [domain]: err.message }));
    } finally {
      setFutureReelLoading(prev => ({ ...prev, [domain]: false }));
    }
  };

  // Fetch AI-Powered Life Insights
  const fetchAiInsights = async () => {
    if (!birthData || insightsLoading) return;
    setInsightsLoading(true);
    setInsightsError(null);
    try {
      const response = await fetch(`${API_URL}/api/ai-story/life-insights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildStoryRequest(),
          language: selectedLanguage
        })
      });
      const result = await response.json();
      if (result.success) {
        setAiInsights(result);
      } else {
        setInsightsError(result.error || 'Failed to generate insights');
      }
    } catch (err) {
      console.error('Error fetching AI insights:', err);
      setInsightsError(err.message);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Fetch AI-Powered Timeline
  const fetchAiTimeline = async (type = 'yearly', year = null, month = null) => {
    if (!birthData || timelineLoading) return;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const now = new Date();
      const targetYear = year || now.getFullYear();
      const targetMonth = month || now.getMonth() + 1;
      
      const endpoint = type === 'monthly' 
        ? `${API_URL}/api/ai-story/timeline/monthly?year=${targetYear}&month=${targetMonth}`
        : `${API_URL}/api/ai-story/timeline/yearly?year=${targetYear}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...buildStoryRequest(),
          language: selectedLanguage
        })
      });
      const result = await response.json();
      if (result.success) {
        setAiTimeline(result);
      } else {
        setTimelineError(result.error || 'Failed to generate timeline');
      }
    } catch (err) {
      console.error('Error fetching AI timeline:', err);
      setTimelineError(err.message);
    } finally {
      setTimelineLoading(false);
    }
  };

  // Auto-fetch when tab changes
  useEffect(() => {
    if (!birthData) return;
    
    if (activeTab === 'past-story' && !aiPastStory && !pastStoryLoading) {
      fetchAiPastStory();
    }
    if (activeTab === 'future-reels' && !aiFutureReels[selectedDomain] && !futureReelLoading[selectedDomain]) {
      fetchAiFutureReel(selectedDomain);
    }
    if (activeTab === 'insights' && !aiInsights && !insightsLoading) {
      fetchAiInsights();
    }
    if (activeTab === 'timelines' && !aiTimeline && !timelineLoading) {
      fetchAiTimeline();
    }
  }, [activeTab, birthData, selectedDomain]);

  // Refetch when language changes for story tabs
  useEffect(() => {
    if (!birthData) return;
    if (activeTab === 'past-story' && aiPastStory && aiPastStory.language !== selectedLanguage) {
      setAiPastStory(null);
      fetchAiPastStory();
    }
    if (activeTab === 'insights' && aiInsights && aiInsights.language !== selectedLanguage) {
      setAiInsights(null);
      fetchAiInsights();
    }
  }, [selectedLanguage]);

  // Fetch future reel when domain changes
  useEffect(() => {
    if (activeTab === 'future-reels' && birthData && !aiFutureReels[selectedDomain] && !futureReelLoading[selectedDomain]) {
      fetchAiFutureReel(selectedDomain);
    }
  }, [selectedDomain]);

  // Domain configuration for 8 domains
  const domainConfig = [
    { key: '1_health_longevity', name: 'Health & Longevity', icon: Activity, color: 'text-red-400' },
    { key: '2_career_profession', name: 'Career & Profession', icon: Target, color: 'text-blue-400' },
    { key: '3_wealth_property_business', name: 'Wealth & Business', icon: Sparkles, color: 'text-yellow-400' },
    { key: '4_family_children', name: 'Family & Children', icon: Users, color: 'text-pink-400' },
    { key: '5_education_skills', name: 'Education & Skills', icon: FileSpreadsheet, color: 'text-green-400' },
    { key: '6_travel_settlement', name: 'Travel & Settlement', icon: Globe, color: 'text-cyan-400' },
    { key: '7_marriage_relationships', name: 'Marriage & Relationships', icon: Users, color: 'text-purple-400' },
    { key: '8_religion_spirituality', name: 'Religion & Spirituality', icon: Star, color: 'text-amber-400' }
  ];

  const tabs = [
    { id: 'past-story', label: 'Past Story', icon: BookOpen },
    { id: 'future-reels', label: 'Future Reels', icon: Play },
    { id: 'insights', label: 'Insights', icon: Lightbulb },
    { id: 'timelines', label: 'Timelines', icon: TrendingUp },
    { id: 'domains', label: 'All Domains', icon: Package },
    { id: 'planets', label: 'D1 Planets', icon: Sun },
    { id: 'houses', label: 'D1 Houses', icon: Home },
    { id: 'kp', label: 'KP System', icon: Grid3X3 },
    { id: 'jaimini', label: 'Jaimini', icon: Compass },
    { id: 'dasha', label: 'Dasha', icon: Calendar },
    { id: 'raw', label: 'Raw JSON', icon: FileJson }
  ];

  const getPlanetColor = (planet) => {
    const colors = {
      Sun: 'text-orange-400', Moon: 'text-gray-300', Mars: 'text-red-400',
      Mercury: 'text-green-400', Jupiter: 'text-yellow-400', Venus: 'text-pink-400',
      Saturn: 'text-blue-400', Rahu: 'text-purple-400', Ketu: 'text-amber-600'
    };
    return colors[planet] || 'text-white';
  };

  // Get domains from kernel data
  const domains = kernelData?.domains || {};
  const meta = kernelData?.meta || {};
  const baseKernel = kernelData?.base_kernel || {};

  // Download single domain
  const downloadSingleDomain = (domainKey, domainData, domainName) => {
    const name = meta?.name || 'kernel';
    const singleDomainData = {
      meta: {
        name: meta?.name,
        birth_data: meta?.birth_data,
        domain: domainName,
        generated_at: new Date().toISOString()
      },
      domain_kernel: domainData
    };
    
    const blob = new Blob([JSON.stringify(singleDomainData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/\s+/g, '_')}_${domainName.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render domain block with collapsible sections
  const renderDomainBlock = (domainKey, domainData, config) => {
    const isExpanded = expandedDomain === domainKey;
    const blockCount = Object.keys(domainData || {}).length;
    
    return (
      <div key={domainKey} className="border border-gray-700 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-4 bg-gray-800/50">
          <button
            onClick={() => setExpandedDomain(isExpanded ? null : domainKey)}
            className="flex items-center gap-3 hover:opacity-80 transition-all flex-1"
          >
            <config.icon className={`w-5 h-5 ${config.color}`} />
            <span className="font-medium text-white">{config.name}</span>
            <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">{blockCount} blocks</span>
            {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          
          {/* Download Single Domain Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              downloadSingleDomain(domainKey, domainData, config.name);
            }}
            className="ml-4 px-3 py-1.5 bg-gray-700 hover:bg-amber-500 hover:text-black text-gray-300 rounded-lg text-xs font-medium transition-all flex items-center gap-1"
            data-testid={`download-${domainKey}`}
            title={`Download ${config.name} kernel`}
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </button>
        </div>
        
        {isExpanded && domainData && (
          <div className="p-4 space-y-4 bg-gray-900/50">
            {/* Key RAW DATA BLOCKS */}
            {domainData.d1_planet_table && (
              <CollapsibleBlock 
                title="D1 Planet Table" 
                data={domainData.d1_planet_table}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_planets`}
              />
            )}
            {domainData.d1_house_table && (
              <CollapsibleBlock 
                title="D1 House Table" 
                data={domainData.d1_house_table}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_houses`}
              />
            )}
            {domainData.lord_matrix && (
              <CollapsibleBlock 
                title="Lord Placement Matrix" 
                data={domainData.lord_matrix}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_lords`}
              />
            )}
            {domainData.kp_cusp_table && (
              <CollapsibleBlock 
                title="KP Cusp Table" 
                data={domainData.kp_cusp_table}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_kp`}
              />
            )}
            {domainData.jaimini_block && (
              <CollapsibleBlock 
                title="Jaimini Block" 
                data={domainData.jaimini_block}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_jaimini`}
              />
            )}
            {domainData.arudha_block && (
              <CollapsibleBlock 
                title="Arudha Padas (A1-A12)" 
                data={domainData.arudha_block}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_arudha`}
              />
            )}
            {domainData.ashtakavarga_block && (
              <CollapsibleBlock 
                title="Ashtakavarga Block" 
                data={domainData.ashtakavarga_block}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_av`}
              />
            )}
            {domainData.dasha_table && (
              <CollapsibleBlock 
                title="Dasha Table" 
                data={domainData.dasha_table}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_dasha`}
              />
            )}
            {domainData.linkage_graph && (
              <CollapsibleBlock 
                title="Linkage Graph" 
                data={domainData.linkage_graph}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_linkage`}
              />
            )}
            {/* Domain-specific layers */}
            {Object.keys(domainData).filter(k => k.startsWith('layer_')).map(layerKey => (
              <CollapsibleBlock 
                key={layerKey}
                title={layerKey.replace(/_/g, ' ').replace('layer ', 'Layer ')} 
                data={domainData[layerKey]}
                expandedBlock={expandedBlock}
                setExpandedBlock={setExpandedBlock}
                blockKey={`${domainKey}_${layerKey}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header with person info */}
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-amber-400">
              {meta?.name || 'Generated Kernel'}
            </h2>
            <p className="text-gray-400 mt-1">
              {meta?.birth_data?.day}/{meta?.birth_data?.month}/{meta?.birth_data?.year} | 
              {` ${meta?.birth_data?.hour}:${String(meta?.birth_data?.minute || 0).padStart(2, '0')}`} | 
              Lat: {meta?.birth_data?.latitude?.toFixed(4)}, Long: {meta?.birth_data?.longitude?.toFixed(4)}
            </p>
            <p className="text-green-400 text-sm mt-2">
              ✅ All 8 Domain Kernels Generated ({Object.keys(domains).length} domains)
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={onDownload}
              className="px-6 py-3 bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-all flex items-center gap-2 font-semibold"
              data-testid="download-kernel-btn"
            >
              <Download className="w-5 h-5" /> Download Complete JSON
            </button>
            <button
              onClick={() => {
                // Download all domains separately
                domainConfig.forEach((config, index) => {
                  setTimeout(() => {
                    const domainData = domains[config.key];
                    if (domainData) {
                      downloadSingleDomain(config.key, domainData, config.name);
                    }
                  }, index * 500); // Stagger downloads
                });
              }}
              className="px-4 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-all flex items-center gap-2 font-medium text-sm"
              data-testid="download-all-separate-btn"
            >
              <Package className="w-4 h-4" /> Download All Separately
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-gray-800/50 rounded-xl p-2">
        <div className="flex overflow-x-auto gap-1 scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-black'
                  : 'text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-800/30 rounded-xl p-6">
        {/* AI-Powered Past Story Tab */}
        {activeTab === 'past-story' && (
          <AiPastStoryContent 
            storyData={aiPastStory}
            loading={pastStoryLoading}
            error={pastStoryError}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            onRefresh={fetchAiPastStory}
            meta={meta}
          />
        )}

        {/* AI-Powered Future Reels Tab */}
        {activeTab === 'future-reels' && (
          <AiFutureReelsContent 
            reels={aiFutureReels}
            loading={futureReelLoading}
            error={futureReelError}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            selectedDomain={selectedDomain}
            setSelectedDomain={setSelectedDomain}
            onFetchReel={fetchAiFutureReel}
            meta={meta}
          />
        )}

        {/* AI-Powered Insights Tab */}
        {activeTab === 'insights' && (
          <AiInsightsContent 
            insightsData={aiInsights}
            loading={insightsLoading}
            error={insightsError}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            onRefresh={fetchAiInsights}
            meta={meta}
          />
        )}

        {/* AI-Powered Timelines Tab */}
        {activeTab === 'timelines' && (
          <AiTimelinesContent 
            timelineData={aiTimeline}
            loading={timelineLoading}
            error={timelineError}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            onFetchTimeline={fetchAiTimeline}
            meta={meta}
            birthData={birthData}
          />
        )}

        {/* Legacy content for non-AI tabs - keeping unchanged */}

        {activeTab === 'domains' && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-amber-400 mb-4">All 8 Domain Raw Kernels</h3>
            {domainConfig.map(config => {
              const domainData = domains[config.key];
              return renderDomainBlock(config.key, domainData, config);
            })}
          </div>
        )}
        
        {activeTab === 'planets' && (
          <D1PlanetTableView domains={domains} getPlanetColor={getPlanetColor} />
        )}
        
        {activeTab === 'houses' && (
          <D1HouseTableView domains={domains} />
        )}
        
        {activeTab === 'kp' && (
          <KPSystemView domains={domains} />
        )}
        
        {activeTab === 'jaimini' && (
          <JaiminiView domains={domains} />
        )}
        
        {activeTab === 'dasha' && (
          <DashaView domains={domains} getPlanetColor={getPlanetColor} />
        )}
        
        {activeTab === 'raw' && (
          <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-[600px]">
            <pre className="text-xs text-gray-300 whitespace-pre-wrap">
              {JSON.stringify(kernelData, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

// Collapsible Block Component
const CollapsibleBlock = ({ title, data, expandedBlock, setExpandedBlock, blockKey }) => {
  const isExpanded = expandedBlock === blockKey;
  
  return (
    <div className="border border-gray-700/50 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpandedBlock(isExpanded ? null : blockKey)}
        className="w-full flex items-center justify-between p-3 bg-gray-800/30 hover:bg-gray-800/50 transition-all text-left"
      >
        <span className="text-sm font-medium text-gray-300">{title}</span>
        {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
      </button>
      {isExpanded && (
        <div className="p-3 bg-gray-900/30 overflow-auto max-h-96">
          <pre className="text-xs text-gray-400 whitespace-pre-wrap">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// D1 Planet Table View
const D1PlanetTableView = ({ domains, getPlanetColor }) => {
  // Get planet data from first domain (same across all)
  const firstDomain = Object.values(domains)[0] || {};
  const planetTable = firstDomain.d1_planet_table || {};
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-amber-400">D1 Planet Table</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="p-3">Planet</th>
              <th className="p-3">Sign</th>
              <th className="p-3">House</th>
              <th className="p-3">Degree</th>
              <th className="p-3">Nakshatra</th>
              <th className="p-3">Pada</th>
              <th className="p-3">Dignity</th>
              <th className="p-3">Retro</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(planetTable).map(([planet, data]) => (
              <tr key={planet} className="border-b border-gray-800 hover:bg-gray-800/30">
                <td className={`p-3 font-medium ${getPlanetColor(planet)}`}>{planet}</td>
                <td className="p-3 text-white">{data.sign}</td>
                <td className="p-3 text-amber-400">{data.house}</td>
                <td className="p-3 text-gray-400">{data.degree?.toFixed(2)}°</td>
                <td className="p-3 text-gray-300">{data.nakshatra}</td>
                <td className="p-3 text-gray-400">{data.pada}</td>
                <td className={`p-3 ${data.dignity?.status === 'EXALTED' ? 'text-green-400' : data.dignity?.status === 'DEBILITATED' ? 'text-red-400' : 'text-gray-400'}`}>
                  {data.dignity?.status || 'N/A'}
                </td>
                <td className="p-3">{data.retrograde ? '℞' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// D1 House Table View
const D1HouseTableView = ({ domains }) => {
  const firstDomain = Object.values(domains)[0] || {};
  const houseTable = firstDomain.d1_house_table || {};
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-amber-400">D1 House Table (H1-H12)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(houseTable).map(([house, data]) => (
          <div key={house} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <span className="text-amber-400 font-bold">{house}</span>
              <span className="text-gray-400 text-sm">{data.sign}</span>
            </div>
            <div className="text-sm space-y-1">
              <p className="text-gray-300">Lord: <span className="text-white">{data.lord}</span> in H{data.lord_house}</p>
              <p className="text-gray-300">Occupants: <span className="text-amber-300">{data.occupants?.join(', ') || 'None'}</span></p>
              <p className="text-gray-500 text-xs">Element: {data.element} | {data.modality}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// KP System View
const KPSystemView = ({ domains }) => {
  const firstDomain = Object.values(domains)[0] || {};
  const kpTable = firstDomain.kp_cusp_table || {};
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-amber-400">KP Cusp Table (Placidus)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="p-3">Cusp</th>
              <th className="p-3">Sign</th>
              <th className="p-3">Nakshatra</th>
              <th className="p-3">Pada</th>
              <th className="p-3">Star Lord (NL)</th>
              <th className="p-3">Sub Lord (SL)</th>
              <th className="p-3">Sub-Sub (SSL)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(kpTable).map(([cusp, data]) => (
              <tr key={cusp} className="border-b border-gray-800 hover:bg-gray-800/30">
                <td className="p-3 text-amber-400 font-medium">{cusp}</td>
                <td className="p-3 text-white">{data.sign}</td>
                <td className="p-3 text-gray-300">{data.nakshatra}</td>
                <td className="p-3 text-gray-400">{data.pada}</td>
                <td className="p-3 text-blue-400">{data.star_lord}</td>
                <td className="p-3 text-green-400">{data.sub_lord}</td>
                <td className="p-3 text-purple-400">{data.sub_sub_lord}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Jaimini View
const JaiminiView = ({ domains }) => {
  const firstDomain = Object.values(domains)[0] || {};
  const jaimini = firstDomain.jaimini_block || {};
  const karakas = jaimini.chara_karakas || {};
  const arudhas = firstDomain.arudha_block || {};
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Chara Karakas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(karakas).map(([karaka, data]) => (
            <div key={karaka} className="bg-gray-800/50 rounded-lg p-4 text-center border border-gray-700">
              <p className="text-amber-400 font-bold">{karaka}</p>
              <p className="text-white text-lg">{data.planet}</p>
              <p className="text-gray-400 text-sm">{data.degree}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Arudha Padas (A1-A12)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {Object.entries(arudhas).map(([arudha, data]) => (
            <div key={arudha} className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
              <p className="text-purple-400 font-bold">{arudha}</p>
              <p className="text-white">H{data.house}</p>
              <p className="text-gray-400 text-xs">{data.sign}</p>
            </div>
          ))}
        </div>
      </div>
      
      {jaimini.special_lagnas && (
        <div>
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Special Lagnas</h3>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <pre className="text-xs text-gray-300">{JSON.stringify(jaimini.special_lagnas, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
};

// Dasha View
const DashaView = ({ domains, getPlanetColor }) => {
  const firstDomain = Object.values(domains)[0] || {};
  const dashaTable = firstDomain.dasha_table || {};
  const mahadashas = dashaTable.mahadashas || [];
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-amber-400">Vimshottari Dasha Timeline</h3>
      {dashaTable.dasha_at_birth && (
        <p className="text-gray-300">Current Dasha at Birth: <span className="text-amber-400">{dashaTable.dasha_at_birth}</span></p>
      )}
      <div className="space-y-2">
        {mahadashas.map((md, idx) => (
          <div key={idx} className="flex items-center gap-4 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <div className={`text-lg font-bold ${getPlanetColor(md.lord)} w-20`}>{md.lord}</div>
            <div className="flex-1">
              <div className="text-white">{md.start_date} → {md.end_date}</div>
              <div className="text-gray-400 text-sm">
                {md.years} years | Lord in H{md.lord_house} | Houses owned: {md.houses_owned?.join(', ')}
                {md.retrograde && <span className="text-red-400 ml-2">℞</span>}
              </div>
            </div>
            <div className={`text-sm ${md.dignity?.status === 'EXALTED' ? 'text-green-400' : md.dignity?.status === 'DEBILITATED' ? 'text-red-400' : 'text-gray-400'}`}>
              {md.dignity?.status || ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Overview Content
const OverviewContent = ({ kernelData, getPlanetColor }) => {
  const lagna = kernelData?.ascendant?.sign || 'N/A';
  const moonSign = kernelData?.planets?.Moon?.sign || 'N/A';
  const sunSign = kernelData?.planets?.Sun?.sign || 'N/A';
  
  // Nakshatra can be in panchang or directly in nakshatra
  const nakshatra = kernelData?.panchang?.nakshatra?.name || kernelData?.nakshatra?.name || 'N/A';
  const nakshatraPada = kernelData?.panchang?.nakshatra?.pada || kernelData?.nakshatra?.pada || '-';
  
  // Panchang data
  const panchang = kernelData?.panchang || {};
  const hinduCalendar = kernelData?.hindu_calendar || {};

  // Check for timezone warning from backend
  const timezoneWarning = kernelData?.birth_particulars?.timezone_warning;

  return (
    <div className="space-y-4">
      {/* Timezone Warning Banner */}
      {timezoneWarning && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-red-400 font-semibold">Timezone Mismatch Warning</h4>
            <p className="text-red-300 text-sm mt-1">{timezoneWarning}</p>
            <p className="text-red-200 text-xs mt-2">This may cause incorrect Lagna, house cusps, and all derived calculations. Please regenerate with correct timezone.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-xl p-6 border border-amber-500/30">
        <h3 className="text-gray-400 text-sm mb-2">Lagna (Ascendant)</h3>
        <p className="text-2xl font-bold text-amber-400">{lagna}</p>
        <p className="text-gray-500 text-sm mt-1">{kernelData?.ascendant?.dms || ''}</p>
      </div>

      <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-xl p-6 border border-blue-500/30">
        <h3 className="text-gray-400 text-sm mb-2">Moon Sign (Rashi)</h3>
        <p className="text-2xl font-bold text-blue-400">{moonSign}</p>
        <p className="text-gray-500 text-sm mt-1">{kernelData?.planets?.Moon?.degree?.toFixed(2) || ''}°</p>
      </div>

      <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-xl p-6 border border-orange-500/30">
        <h3 className="text-gray-400 text-sm mb-2">Sun Sign</h3>
        <p className="text-2xl font-bold text-orange-400">{sunSign}</p>
        <p className="text-gray-500 text-sm mt-1">{kernelData?.planets?.Sun?.degree?.toFixed(2) || ''}°</p>
      </div>

      <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-6 border border-purple-500/30">
        <h3 className="text-gray-400 text-sm mb-2">Nakshatra</h3>
        <p className="text-2xl font-bold text-purple-400">{nakshatra}</p>
        <p className="text-gray-500 text-sm mt-1">Pada {nakshatraPada}</p>
      </div>

      {/* Panchang */}
      <div className="col-span-full bg-gray-800/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-amber-400" />
          Panchang
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <p className="text-gray-400 text-sm">Tithi</p>
            <p className="text-white font-medium">{kernelData?.tithi?.full_name || kernelData?.tithi?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Nakshatra</p>
            <p className="text-white font-medium">{kernelData?.nakshatra?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Yoga</p>
            <p className="text-white font-medium">{kernelData?.yoga?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Karana</p>
            <p className="text-white font-medium">{kernelData?.karana?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Day</p>
            <p className="text-white font-medium">{kernelData?.day_info?.english || kernelData?.day_info?.name || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Special Lagnas */}
      <div className="col-span-full bg-gray-800/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-amber-400" />
          Special Lagnas
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-gray-400 text-sm">Indu Lagna</p>
            <p className="text-white font-medium">{kernelData?.indu_lagna?.sign || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Hora Lagna</p>
            <p className="text-white font-medium">{kernelData?.hora_lagna?.sign || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Ghati Lagna</p>
            <p className="text-white font-medium">{kernelData?.ghati_lagna?.sign || 'N/A'}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Arudha Lagna</p>
            <p className="text-white font-medium">{kernelData?.arudha_lagna?.sign || 'N/A'}</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// Charts Content
const ChartsContent = ({ kernelData, showDegrees }) => {
  const [selectedChart, setSelectedChart] = useState('D1_Rashi');
  
  const chartOptions = [
    { key: 'D1_Rashi', label: 'D1 Rashi' },
    { key: 'D9_Navamsa', label: 'D9 Navamsa' },
    { key: 'D10_Dasamsa', label: 'D10 Dasamsa' },
    { key: 'D2_Hora', label: 'D2 Hora' },
    { key: 'D3_Drekkana', label: 'D3 Drekkana' },
    { key: 'D7_Saptamsa', label: 'D7 Saptamsa' },
    { key: 'D12_Dwadasamsa', label: 'D12 Dwadasamsa' },
    { key: 'D60_Shastiamsa', label: 'D60 Shastiamsa' },
  ];

  const getChartData = () => {
    const chart = kernelData?.divisional_charts?.[selectedChart] || {};
    return chart;
  };

  const chartData = getChartData();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {chartOptions.map(opt => (
          <button
            key={opt.key}
            onClick={() => setSelectedChart(opt.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedChart === opt.key
                ? 'bg-amber-500 text-black'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="bg-gray-800/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">{selectedChart.replace('_', ' ')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(chartData).map(([planet, data]) => (
            <div key={planet} className="bg-gray-900/50 rounded-lg p-3">
              <p className="text-gray-400 text-sm">{planet}</p>
              <p className="text-white font-medium">{data?.sign || 'N/A'}</p>
              {showDegrees && data?.longitude && (
                <p className="text-gray-500 text-xs">{data.longitude.toFixed(2)}°</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Planets Content
const PlanetsContent = ({ kernelData, getPlanetColor, showDegrees }) => {
  const planets = kernelData?.planets || {};

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-900">
            <th className="px-4 py-3 text-left text-gray-400">Planet</th>
            <th className="px-4 py-3 text-left text-gray-400">Sign</th>
            <th className="px-4 py-3 text-left text-gray-400">House</th>
            <th className="px-4 py-3 text-left text-gray-400">Degree</th>
            <th className="px-4 py-3 text-left text-gray-400">Nakshatra</th>
            <th className="px-4 py-3 text-left text-gray-400">Status</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(planets).map(([planet, data]) => (
            <tr key={planet} className="border-t border-gray-700">
              <td className={`px-4 py-3 font-medium ${getPlanetColor(planet)}`}>{planet}</td>
              <td className="px-4 py-3 text-white">{data?.sign || 'N/A'}</td>
              <td className="px-4 py-3 text-gray-300">{data?.house || 'N/A'}</td>
              <td className="px-4 py-3 text-gray-300">{data?.degree?.toFixed(2) || 'N/A'}°</td>
              <td className="px-4 py-3 text-purple-400">{data?.nakshatra || 'N/A'}</td>
              <td className="px-4 py-3">
                {data?.retrograde && <span className="text-red-400">R</span>}
                {data?.combust && <span className="text-orange-400 ml-2">Combust</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Houses Content
const HousesContent = ({ kernelData, getPlanetColor }) => {
  const houses = kernelData?.house_signs || {};

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Object.entries(houses).map(([house, data]) => (
        <div key={house} className="bg-gray-800/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-amber-400 font-bold">House {house}</span>
            <span className="text-white">{data?.sign || 'N/A'}</span>
          </div>
          <p className="text-gray-400 text-sm">Lord: {data?.lord || 'N/A'}</p>
          {data?.planets && data.planets.length > 0 && (
            <p className="text-gray-300 text-sm mt-1">
              Planets: {data.planets.join(', ')}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

// Dasha Content
const DashaContent = ({ kernelData, getPlanetColor }) => {
  const dashaAtBirth = kernelData?.dasha_at_birth || 'N/A';
  const dashaBalance = kernelData?.dasha_balance || {};
  const mahadashas = kernelData?.mahadashas || [];

  return (
    <div className="space-y-6">
      {/* Current Dasha */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Birth Dasha</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-400 text-sm">Dasha at Birth</p>
            <p className="text-2xl font-bold text-purple-400">{dashaAtBirth}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">Dasha Balance</p>
            <p className="text-xl font-bold text-white">{dashaBalance?.formatted || 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Mahadasha List */}
      <div className="bg-gray-800/50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Vimshottari Mahadashas</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900">
                <th className="px-4 py-3 text-left text-gray-400">Planet</th>
                <th className="px-4 py-3 text-left text-gray-400">Years</th>
                <th className="px-4 py-3 text-left text-gray-400">Start Date</th>
                <th className="px-4 py-3 text-left text-gray-400">End Date</th>
              </tr>
            </thead>
            <tbody>
              {mahadashas.slice(0, 9).map((md, idx) => (
                <tr key={idx} className="border-t border-gray-700">
                  <td className={`px-4 py-3 font-medium ${getPlanetColor(md?.lord)}`}>{md?.lord || 'N/A'}</td>
                  <td className="px-4 py-3 text-white">{md?.years?.toFixed(1) || 'N/A'}</td>
                  <td className="px-4 py-3 text-gray-300">{md?.start_date || 'N/A'}</td>
                  <td className="px-4 py-3 text-gray-300">{md?.end_date || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Shadbala Content
const ShadbalaContent = ({ kernelData, getPlanetColor }) => {
  const shadbala = kernelData?.shadbala || {};

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-900">
            <th className="px-4 py-3 text-left text-gray-400">Planet</th>
            <th className="px-4 py-3 text-left text-gray-400">Total Shadbala</th>
            <th className="px-4 py-3 text-left text-gray-400">Sthana Bala</th>
            <th className="px-4 py-3 text-left text-gray-400">Dig Bala</th>
            <th className="px-4 py-3 text-left text-gray-400">Kaala Bala</th>
            <th className="px-4 py-3 text-left text-gray-400">Min Required</th>
            <th className="px-4 py-3 text-left text-gray-400">Status</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(shadbala).map(([planet, data]) => (
            <tr key={planet} className="border-t border-gray-700">
              <td className={`px-4 py-3 font-medium ${getPlanetColor(planet)}`}>{planet}</td>
              <td className="px-4 py-3 text-white font-bold">{data?.total_shadbala?.toFixed(1) || 'N/A'}</td>
              <td className="px-4 py-3 text-gray-300">{data?.sthana_bala?.toFixed(1) || 'N/A'}</td>
              <td className="px-4 py-3 text-gray-300">{data?.dig_bala?.toFixed(1) || 'N/A'}</td>
              <td className="px-4 py-3 text-gray-300">{data?.kaala_bala?.toFixed(1) || 'N/A'}</td>
              <td className="px-4 py-3 text-gray-400">{data?.min_requirement || 'N/A'}</td>
              <td className="px-4 py-3">
                {data?.total_shadbala >= (data?.min_requirement || 0) ? (
                  <span className="text-green-400">Strong</span>
                ) : (
                  <span className="text-red-400">Weak</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Ashtakavarga Content
const AshtakavargaContent = ({ kernelData }) => {
  const sav = kernelData?.sarvashtakavarga || [];
  const signs = ['Ari', 'Tau', 'Gem', 'Can', 'Leo', 'Vir', 'Lib', 'Sco', 'Sag', 'Cap', 'Aqu', 'Pis'];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">Sarvashtakavarga (SAV)</h3>
      <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
        {sav.map((val, idx) => (
          <div key={idx} className={`p-3 rounded-lg text-center ${
            val >= 30 ? 'bg-green-500/20 border border-green-500/30' :
            val >= 25 ? 'bg-amber-500/20 border border-amber-500/30' :
            'bg-red-500/20 border border-red-500/30'
          }`}>
            <p className="text-xs text-gray-400">{signs[idx]}</p>
            <p className="text-lg font-bold text-white">{val}</p>
          </div>
        ))}
      </div>
      <p className="text-gray-400 text-sm">Total: {sav.reduce((a, b) => a + b, 0)}</p>
    </div>
  );
};

// Yogas Content
const YogasContent = ({ kernelData }) => {
  const yogas = kernelData?.yogas || {};
  const presentYogas = yogas?.present_yogas || [];
  const specialYogas = yogas?.special_yogas || [];

  return (
    <div className="space-y-6">
      {presentYogas.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Present Yogas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {presentYogas.map((yoga, idx) => (
              <div key={idx} className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 font-medium">{yoga?.name || yoga}</p>
                {yoga?.description && <p className="text-gray-400 text-sm mt-1">{yoga.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {specialYogas.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-4">Special Yogas</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {specialYogas.map((yoga, idx) => (
              <div key={idx} className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-purple-400 font-medium">{yoga?.name || yoga}</p>
                {yoga?.description && <p className="text-gray-400 text-sm mt-1">{yoga.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {presentYogas.length === 0 && specialYogas.length === 0 && (
        <p className="text-gray-400">No yogas data available</p>
      )}
    </div>
  );
};

// Raw JSON Content
const RawJSONContent = ({ kernelData }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-all"
      >
        {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        {expanded ? 'Collapse' : 'Expand'} Raw JSON
      </button>
      {expanded && (
        <pre className="bg-gray-900 rounded-xl p-4 overflow-auto max-h-96 text-xs text-gray-300">
          {JSON.stringify(kernelData, null, 2)}
        </pre>
      )}
    </div>
  );
};

// Main Page Component
const DynamicKernelPage = () => {
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'
  const [loading, setLoading] = useState(false);
  const [kernelData, setKernelData] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [currentBirthData, setCurrentBirthData] = useState(null);

  // Handle profile selection from search
  const handleSelectProfile = (profile) => {
    setSelectedProfile(profile);
    setKernelData(null); // Clear previous kernel data
    setCurrentBirthData(null);
  };

  const generateSingleKernel = async (birthData) => {
    setLoading(true);
    setCurrentBirthData(birthData); // Save birth data for narrative generation
    try {
      // Use new unified kernel API for all 8 domain raw kernels
      const response = await fetch(`${API_URL}/api/unified-kernel/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: birthData.name,
          year: birthData.year,
          month: birthData.month,
          day: birthData.day,
          hour: birthData.hour,
          minute: birthData.minute,
          latitude: birthData.latitude,
          longitude: birthData.longitude,
          timezone_offset: birthData.timezone_offset,
          gender: birthData.gender || 'male'
        })
      });

      if (!response.ok) throw new Error('Failed to generate kernel');
      
      const result = await response.json();
      if (result.success) {
        setKernelData(result.data);
      } else {
        throw new Error(result.detail || 'Kernel generation failed');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to generate kernel: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const processBulkKernels = async (peopleData) => {
    setBulkLoading(true);
    setBulkResults([]);

    const results = [];
    
    for (const person of peopleData) {
      try {
        // Use new unified kernel API
        const response = await fetch(`${API_URL}/api/unified-kernel/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: person.name,
            year: person.year,
            month: person.month,
            day: person.day,
            hour: person.hour,
            minute: person.minute,
            latitude: person.latitude,
            longitude: person.longitude,
            timezone_offset: person.timezone_offset,
            gender: person.gender || 'male'
          })
        });

        if (!response.ok) throw new Error('API Error');
        
        const result = await response.json();
        if (result.success) {
          results.push({
            name: person.name,
            success: true,
            data: result.data
          });
        } else {
          throw new Error(result.detail || 'Failed');
        }
      } catch (err) {
        results.push({
          name: person.name,
          success: false,
          error: err.message
        });
      }
    }

    setBulkResults(results);
    setBulkLoading(false);
  };

  const downloadKernel = () => {
    if (!kernelData) return;
    const name = kernelData?.meta?.name || kernelData?.name || 'kernel';
    const blob = new Blob([JSON.stringify(kernelData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/\s+/g, '_')}_raw_kernel_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur border-b border-amber-500/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-amber-400">
                Raw Kernel Generator - All 8 Domains
              </h1>
              <p className="text-gray-400 text-sm">
                Generate complete RAW DATA KERNELS for any birth data (D1, KP, Jaimini, Arudha, Dasha & more)
              </p>
            </div>

            {/* Mode Toggle */}
            <div className="flex bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setMode('single')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  mode === 'single' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid="mode-single"
              >
                <User className="w-4 h-4" />
                Single Person
              </button>
              <button
                onClick={() => setMode('bulk')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                  mode === 'bulk' ? 'bg-amber-500 text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid="mode-bulk"
              >
                <Users className="w-4 h-4" />
                Bulk Upload
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Celebrity Database Download Section - Always Visible */}
        <div className="mb-8">
          <CelebrityDownloadSection />
        </div>

        {mode === 'single' ? (
          <div className="space-y-8">
            {/* Profile Search Section */}
            <div className="bg-gradient-to-r from-amber-900/20 to-orange-900/20 rounded-2xl p-6 border border-amber-500/30">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Search className="w-5 h-5 text-amber-400" />
                Quick Select from Saved Profiles
              </h2>
              <ProfileSearch onSelectProfile={handleSelectProfile} />
              {selectedProfile && (
                <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <span className="text-amber-400 text-sm">
                    Selected: <strong className="text-white">{selectedProfile.name}</strong> - {selectedProfile.day}/{selectedProfile.month}/{selectedProfile.year} | {selectedProfile.place}
                  </span>
                </div>
              )}
            </div>

            {/* Birth Details Form */}
            <div className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-amber-400" />
                Enter Birth Details
                {selectedProfile && <span className="text-sm text-gray-400 ml-2">(Auto-filled from selected profile)</span>}
              </h2>
              <BirthDetailsForm onSubmit={generateSingleKernel} loading={loading} initialData={selectedProfile} />
            </div>

            {/* Generated Kernel Display */}
            {kernelData && (
              <KernelDisplayTabs kernelData={kernelData} onDownload={downloadKernel} birthData={currentBirthData} />
            )}
          </div>
        ) : (
          <div className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-amber-400" />
              Bulk Kernel Generation
            </h2>
            <BulkUploadSection 
              onBulkProcess={processBulkKernels} 
              bulkLoading={bulkLoading}
              bulkResults={bulkResults}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DynamicKernelPage;
